import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Contract, contractsApi } from '../../api/contractsApi';
import { formatVndAmount } from '../../utils/vndFormat';
import {
  formatContractAmountField,
  parseContractAmountField,
} from '../../utils/contractAmount.util';
import { getApiErrorMessage } from '../../utils/apiErrors';

type LineRow = { billingPeriod: string; amount: string };

interface ManualContractInvoicesModalProps {
  contract: Contract;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const emptyLine = (): LineRow => ({ billingPeriod: '', amount: '' });

export const ManualContractInvoicesModal: React.FC<ManualContractInvoicesModalProps> = ({
  contract,
  open,
  onClose,
  onSuccess,
}) => {
  const [lines, setLines] = useState<LineRow[]>([emptyLine(), emptyLine()]);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLines([emptyLine(), emptyLine()]);
    setReplaceExisting(false);
  }, [open, contract.id]);

  const lineTotal = useMemo(
    () => lines.reduce((sum, row) => sum + parseContractAmountField(row.amount), 0),
    [lines],
  );

  const contractValue = Math.round(contract.valueWithVat ?? 0);
  const delta = contractValue - lineTotal;

  const updateLine = (index: number, patch: Partial<LineRow>) => {
    setLines((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index: number) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async () => {
    const payload = lines
      .map((row) => ({
        billingPeriod: row.billingPeriod.trim(),
        amount: parseContractAmountField(row.amount),
      }))
      .filter((row) => row.billingPeriod && row.amount > 0);

    if (payload.length === 0) {
      toast.error('Nhập ít nhất một kỳ và số tiền');
      return;
    }
    if (lineTotal !== contractValue) {
      toast.error(
        `Tổng các kỳ (${formatVndAmount(lineTotal)}) phải khớp giá trị HĐ (${formatVndAmount(contractValue)})`,
      );
      return;
    }

    const priorNote = contract.lastRecordedBillingPeriod
      ? `\nKỳ ≤ ${contract.lastRecordedBillingPeriod} sẽ ghi Đã thanh toán (đã thu trước HT).`
      : '';

    if (
      !window.confirm(
        `Tạo ${payload.length} kỳ cho HĐ ${contract.code}?\nTổng: ${formatVndAmount(lineTotal)}${priorNote}`,
      )
    ) {
      return;
    }

    setSaving(true);
    try {
      await contractsApi.createManualContractInvoices(contract.id, {
        lines: payload,
        replaceExisting,
      });
      toast.success('Đã tạo hóa đơn theo lịch thủ công');
      onSuccess();
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Không thể tạo hóa đơn thủ công'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Tạo HĐ thủ công</h2>
          <p className="text-sm text-gray-600 mt-1">
            {contract.code} · Giá trị HĐ: {formatVndAmount(contractValue)}
            {contract.lastRecordedBillingPeriod
              ? ` · Kỳ thu cuối (HT): ${contract.lastRecordedBillingPeriod}`
              : null}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Mỗi căn trên HĐ nhận cùng số tiền theo kỳ. Sau khi lưu, Tạo HĐ tự động sẽ không ghi đè khi tổng đã khớp.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {lines.map((row, index) => (
            <div key={index} className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Kỳ (MM/YYYY)</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="10/2026"
                  value={row.billingPeriod}
                  onChange={(e) => updateLine(index, { billingPeriod: e.target.value })}
                />
              </div>
              <div className="flex-[2] min-w-[160px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Số tiền (VNĐ)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={row.amount}
                  onChange={(e) =>
                    updateLine(index, { amount: e.target.value.replace(/[^\d]/g, '') })
                  }
                  onBlur={() =>
                    updateLine(index, { amount: formatContractAmountField(row.amount) })
                  }
                />
              </div>
              <button
                type="button"
                className="text-sm text-red-600 px-2 py-2"
                onClick={() => removeLine(index)}
              >
                Xóa
              </button>
            </div>
          ))}
          <button type="button" className="text-sm text-blue-600" onClick={addLine}>
            + Thêm kỳ
          </button>

          <div
            className={`text-sm font-medium pt-2 ${
              delta === 0 ? 'text-emerald-700' : 'text-amber-800'
            }`}
          >
            Tổng các kỳ: {formatVndAmount(lineTotal)}
            {delta !== 0 ? ` · Còn lệch ${formatVndAmount(Math.abs(delta))} so với HĐ` : ' · Khớp giá trị HĐ'}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 pt-2">
            <input
              type="checkbox"
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
            />
            Thay thế toàn bộ hóa đơn HĐ hiện có (chỉ khi chưa có phiếu thu)
          </label>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <button type="button" className="px-4 py-2 border rounded" onClick={onClose} disabled={saving}>
            Hủy
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-purple-600 text-white rounded disabled:opacity-50"
            onClick={handleSubmit}
            disabled={saving || delta !== 0}
          >
            {saving ? 'Đang lưu…' : 'Tạo hóa đơn'}
          </button>
        </div>
      </div>
    </div>
  );
};
